import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Box, Card, ListRow, Separator, Text, Tooltip } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';

import { EmployeeFourOhOneKContributionsResult, EmployersResult, MessagesResult } from './data';
import { useFourOhOneKContributionsQuery } from './hooks';

import { EmployerFourOhOneKContributionFrequency } from '../graphql/schema';
import MessageBox from '../Shared/components-v2/message-box/message-box';
import { urlBuilder } from '../Shared/urlBuilder';

const enum FourOhOneKMaxOutStatuses {
  NotProjectedToMax,
  ProjectedToMax,
  Maxed,
}

const FourOhOneKLimitMaxOutProjection = (
  employeeContributions: EmployeeFourOhOneKContributionsResult
): FourOhOneKMaxOutStatuses => {
  const contributionLimit = employeeContributions.thisYear.taxYear.fourOhOneKContributionLimit.cents;
  const currentContributionAmount = employeeContributions.thisYear.result.amount.cents;
  const projectedContributionAmount = employeeContributions.thisYear.result.projection?.total?.cents || 0;

  if (currentContributionAmount >= contributionLimit) {
    return FourOhOneKMaxOutStatuses.Maxed;
  }
  if (projectedContributionAmount >= contributionLimit) {
    return FourOhOneKMaxOutStatuses.ProjectedToMax;
  }
  return FourOhOneKMaxOutStatuses.NotProjectedToMax;
};

const FourOhOneKLimitTooltipCopy = (employeeContributions: EmployeeFourOhOneKContributionsResult): string => {
  if (employeeContributions.thisYear.taxYear.isEligibleForFourOhOneKContributionCatchUp) {
    return 'Limit includes maximum standard contribution amount as well as catch-up contributions for individuals 50 and over.';
  }
  return 'Limit includes maximum standard contribution amount.';
};

const matchIsAnnual = (employer: EmployersResult[number]): boolean => {
  if (!employer.contributionConfig) {
    return false;
  }
  return employer.contributionConfig.frequency === EmployerFourOhOneKContributionFrequency.Annual;
};

const displayMatchAmount = (employer: EmployersResult[number]): number => {
  if (!employer.fourOhOneKContributions) {
    return 0;
  }
  if (matchIsAnnual(employer)) {
    return employer.fourOhOneKContributions.thisYear.projection?.total?.cents || 0;
  }
  return employer.fourOhOneKContributions.thisYear.amount.cents || 0;
};

const willRecieveFullMatch = (employer: EmployersResult[number]): boolean => {
  if (!employer.contributionConfig) {
    return false;
  }
  return displayMatchAmount(employer) >= employer.contributionConfig.limit?.cents;
};

const AnnualMatchTooltipCopy = (taxYear: number): string => {
  return `Your projected match is based on your ${taxYear} contributions, and will be paid in ${taxYear + 1}.`;
};

const hideEmployerMatch = (employer: EmployersResult[number]): boolean => {
  return !employer.contributionConfig && !employer.fourOhOneKContributions?.thisYear?.amount?.cents;
};

const maxMatchText = (employer: EmployersResult[number]): string => {
  if (matchIsAnnual(employer)) {
    return 'Projected to receive full match';
  }
  return 'Receiving full match';
};

const EmployerMatchHeader = (
  employer: EmployersResult[number],
  {
    thisYear: {
      taxYear: { year },
    },
  }: EmployeeFourOhOneKContributionsResult
) => {
  const { soloFourOhOneKPlan } = employer;

  if (matchIsAnnual(employer)) {
    return (
      <Box css={{ marginRight: 'space.400', display: 'grid' }}>
        <Box css={{ display: 'flex', gap: 'space.50' }}>
          <Text>Projected employer match</Text>
          <Tooltip>
            <Tooltip.Trigger asChild={true}>
              <Tooltip.IconButton data-testid="matchTooltip" />
            </Tooltip.Trigger>
            <Tooltip.Content align="start" side="left">
              <Tooltip.Text>{AnnualMatchTooltipCopy(year)}</Tooltip.Text>
            </Tooltip.Content>
          </Tooltip>
        </Box>
        {willRecieveFullMatch(employer) && (
          <Text color="text.success" variant="text50">
            {maxMatchText(employer)}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Text>
      {year} employer {soloFourOhOneKPlan ? 'contributions' : 'match'}
    </Text>
  );
};

const FourOhOneKContributionsSummary: React.FC<{
  employeeContributions: EmployeeFourOhOneKContributionsResult;
  employers: EmployersResult;
  messages: MessagesResult;
  hasSolo401k: boolean;
}> = ({ employeeContributions, employers, messages, hasSolo401k }) => {
  const status = FourOhOneKLimitMaxOutProjection(employeeContributions);
  const IraContributionTooltipLimitCopy = FourOhOneKLimitTooltipCopy(employeeContributions);

  return (
    <Box>
      <ListRow css={{ padding: 'space.25' }}>
        <ListRow.LeadingContent>
          <Box css={{ display: 'flex', flexDirection: 'row' }}>
            <Text>
              {employeeContributions.thisYear.taxYear.year} {hasSolo401k && 'employee'} contributions
            </Text>
            <Tooltip>
              <Tooltip.Trigger asChild={true} style={{ paddingLeft: '4px' }}>
                <Tooltip.IconButton data-testid="contributionTooltip" />
              </Tooltip.Trigger>
              <Tooltip.Content align="start" side="right">
                <Tooltip.Text>{IraContributionTooltipLimitCopy}</Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          </Box>
          {status === FourOhOneKMaxOutStatuses.Maxed && (
            <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
              Maxed out
            </Text>
          )}
          {status === FourOhOneKMaxOutStatuses.ProjectedToMax && (
            <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
              Projected to max out
            </Text>
          )}
          {status === FourOhOneKMaxOutStatuses.NotProjectedToMax && (
            <Text css={{ textAlign: 'left' }} color="text.weak" variant="text50">
              Limit: {formatMoney(employeeContributions.thisYear.taxYear.fourOhOneKContributionLimit)}
            </Text>
          )}
        </ListRow.LeadingContent>
        <ListRow.TrailingContent>
          <Text css={{ textAlign: 'right' }}>{formatMoney(employeeContributions.thisYear.result.amount)}</Text>
        </ListRow.TrailingContent>
      </ListRow>
      {employers.map(employer => {
        if (hideEmployerMatch(employer)) {
          return null;
        }

        return (
          <Box key={employer.id}>
            <Separator css={{ my: 'space.200' }} />
            <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                {EmployerMatchHeader(employer, employeeContributions)}
                {employers.length > 1 && (
                  <Text color="text.weak" variant="text50">
                    {employer.name}
                  </Text>
                )}
              </Box>
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                <Text css={{ textAlign: 'right' }}>{formatMoney(toMoney(displayMatchAmount(employer)))}</Text>
              </Box>
            </Box>
          </Box>
        );
      })}
      <Separator css={{ my: 'space.200' }} />
      <MessageBox messages={messages} location="goal_overview_four_oh_one_k_contributions">
        <MessageBox.After>
          <Separator css={{ my: 'space.200' }} />
        </MessageBox.After>
      </MessageBox>
      <SambaAutoLink to={urlBuilder.appFourOhOneKDetails.absolute()}>See details</SambaAutoLink>
    </Box>
  );
};

const hasEmployerMatch = (employers: EmployersResult): boolean => {
  return employers.some(employer => employer.fourOhOneKContributions);
};

const FourOhOneKContributionsHeader: React.FC<{
  employers: EmployersResult;
  hasOnlySolo401k: boolean;
}> = ({ employers, hasOnlySolo401k }) => {
  if (hasOnlySolo401k) {
    return (
      <Text css={{ fontWeight: 600, textStyle: { base: 'text100', md: 'text200' } }}>Solo 401(k) contributions</Text>
    );
  }
  return (
    <Text css={{ fontWeight: 600, textStyle: { base: 'text100', md: 'text200' } }}>
      401(k) contributions {hasEmployerMatch(employers) ? ' and match' : ''}
    </Text>
  );
};

const Controller: React.FC<React.PropsWithChildren> = () => {
  const { isLoading, employeeContributions, employers, messages } = useFourOhOneKContributionsQuery();

  if (isLoading) return <CardLoader />;
  if (!employeeContributions || !employers) return null;

  const hasSolo401k: boolean = employers.some(employer => employer.soloFourOhOneKPlan);
  const hasOnlySolo401k: boolean = employers.every(employer => employer.soloFourOhOneKPlan);

  return (
    <Card css={{ padding: 'space.300', display: 'grid', gap: 'space.300' }}>
      <Card.Header>
        <FourOhOneKContributionsHeader employers={employers} hasOnlySolo401k={hasOnlySolo401k} />
      </Card.Header>
      <Separator />
      <Card.Content>
        <FourOhOneKContributionsSummary
          employeeContributions={employeeContributions}
          employers={employers}
          messages={messages!}
          hasSolo401k={hasSolo401k}
        />
      </Card.Content>
    </Card>
  );
};

export default Controller;
